import { useEffect, useMemo, useState, useCallback } from "react";
import { Loader2, Star, Check, Search, Info } from "lucide-react";
import { api, fmtErr } from "../../../lib/api";
import { Input } from "../../../components/ui/input";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../../components/ui/dialog";
import { toast } from "sonner";
import { SYSTEMS } from "./constants";
import { SupplierDetail } from "../Marketplace";

// Small profile dialog for PRIVATE (address-book) suppliers that have no
// marketplace master profile.
function PrivateSupplierInfo({ supplier, onClose }) {
  if (!supplier) return null;
  const s = supplier;
  const rows = [
    ["Location", [s.city, s.country].filter(Boolean).join(", ")],
    ["Categories", (s.categories || []).filter(Boolean).join(", ") || (s.category || "")],
    ["Contact person", s.contact_person || s.contact_name],
    ["Email", s.email],
    ["Phone", s.phone],
    ["Website", s.website],
    ["Address", s.address],
    ["Notes", s.notes],
  ].filter(([, v]) => v);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="private-supplier-info">
        <DialogHeader>
          <DialogTitle>{s.name || s.company_name || "Supplier"}</DialogTitle>
          <DialogDescription>Supplier from your private network (offline)</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 text-sm">
          {rows.length === 0 && <p className="text-zinc-500">No further details on file for this supplier.</p>}
          {rows.map(([label, value]) => (
            <div key={label}>
              <div className="text-[10px] uppercase tracking-wider text-zinc-400">{label}</div>
              <div className="text-zinc-800 break-words">{value}</div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// "Find supplier" assignee mode for the task-create form. Lets a senior crew
// member search suppliers matching the task Category, filter (name / country /
// city / online-offline / rating) and multi-select them to request quotes.
// Selection is lifted to the parent form as `_quote_suppliers`.
export function FindSupplierPicker({ yachtId, defaultCategory, selected = [], onChange, networkOnly = false }) {
  const [q, setQ] = useState("");
  const [category, setCategory] = useState(defaultCategory || "");
  const [country, setCountry] = useState("");
  const [city, setCity] = useState("");
  const [kind, setKind] = useState("all");      // all | marketplace | private
  const [minRating, setMinRating] = useState(0);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  // (i) profile preview — marketplace profiles use the rich SupplierDetail,
  // plain address-book suppliers get a compact info dialog.
  const [profile, setProfile] = useState(null);        // marketplace detail
  const [privProfile, setPrivProfile] = useState(null); // private detail
  const [infoBusy, setInfoBusy] = useState(null);       // row id being loaded

  const openInfo = async (e, r) => {
    e.stopPropagation();
    setInfoBusy(r.id);
    try {
      if (r.kind === "marketplace" || r.master_id) {
        const { data } = await api.get(`/marketplace/suppliers/${r.kind === "marketplace" ? r.id : r.master_id}`);
        setProfile(data);
      } else {
        const { data } = await api.get(`/suppliers/${r.id}`);
        setPrivProfile(data);
      }
    } catch {
      setPrivProfile(r); // fall back to what the row already knows
    } finally {
      setInfoBusy(null);
    }
  };

  const selMap = useMemo(() => {
    const m = {};
    for (const s of selected) m[`${s.kind}:${s.id}`] = s;
    return m;
  }, [selected]);

  const load = useCallback(async () => {
    if (!yachtId) return;
    setLoading(true);
    try {
      const params = { yacht_id: yachtId };
      if (category) params.category = category;
      if (q.trim()) params.q = q.trim();
      if (country.trim()) params.country = country.trim();
      if (city.trim()) params.city = city.trim();
      if (kind !== "all") params.kind = kind;
      if (networkOnly) params.network = true;
      if (minRating) params.min_rating = minRating;
      const { data } = await api.get("/supplier-candidates", { params });
      setRows(data);
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setLoading(false);
    }
  }, [yachtId, category, q, country, city, kind, minRating, networkOnly]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  // Country dropdown options from the current result set (+ whatever is picked).
  const countryOptions = useMemo(() => {
    const set = new Set(rows.map((r) => (r.country || "").trim()).filter(Boolean));
    if (country.trim()) set.add(country.trim());
    return Array.from(set).sort();
  }, [rows, country]);

  // User rule: Online suppliers first (alphabetical), then Offline (alphabetical).
  const isOnline = (r) => r.kind === "marketplace" && r.supplier_mode !== "offline";
  const sortedRows = useMemo(() => rows.slice().sort((a, b) => {
    const ao = isOnline(a) ? 0 : 1;
    const bo = isOnline(b) ? 0 : 1;
    if (ao !== bo) return ao - bo;
    return (a.name || "").localeCompare(b.name || "");
  }), [rows]);

  const toggle = (r) => {
    const k = `${r.kind}:${r.id}`;
    const next = { ...selMap };
    if (next[k]) delete next[k];
    else next[k] = { kind: r.kind, id: r.id, name: r.name, mode: isOnline(r) ? "online" : "offline" };
    onChange(Object.values(next));
  };

  const selCount = selected.length;

  return (
    <div className="space-y-3 rounded-md border border-zinc-200 p-3 bg-zinc-50/50 min-w-0 w-full" data-testid="find-supplier-picker">
      <div className="text-[11px] text-zinc-500">
        {networkOnly
          ? <>Your network suppliers. Select one or more to request a quote — they go through the Supplier Hub, exactly like <span className="font-medium">Find supplier</span>.</>
          : <>Search suppliers and select one or more to request a quote. They'll appear under the
            task's <span className="font-medium">Supplier quotes</span> tab.</>}
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <div className="relative sm:col-span-2">
          <Search className="h-4 w-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name…" className="pl-9 h-9" data-testid="fs-search" />
        </div>
        <select className="h-9 w-full rounded-md border border-input px-3 text-sm bg-white" value={category} onChange={(e) => setCategory(e.target.value)} data-testid="fs-category">
          <option value="">All categories</option>
          {SYSTEMS.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select className="h-9 w-full rounded-md border border-input px-3 text-sm bg-white" value={kind} onChange={(e) => setKind(e.target.value)} data-testid="fs-kind">
          <option value="all">Online &amp; Offline</option>
          <option value="marketplace">Online only</option>
          <option value="private">Offline only</option>
        </select>
        <select className="h-9 w-full rounded-md border border-input px-3 text-sm bg-white" value={country} onChange={(e) => setCountry(e.target.value)} data-testid="fs-country">
          <option value="">Any country</option>
          {countryOptions.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <Input value={city} onChange={(e) => setCity(e.target.value)} placeholder="City" className="h-9" data-testid="fs-city" />
        <select className="h-9 w-full rounded-md border border-input px-3 text-sm bg-white sm:col-span-2" value={minRating} onChange={(e) => setMinRating(Number(e.target.value))} data-testid="fs-min-rating">
          <option value={0}>Any rating</option>
          <option value={3}>3★ &amp; up</option>
          <option value={4}>4★ &amp; up</option>
          <option value={4.5}>4.5★ &amp; up</option>
        </select>
      </div>

      {/* Results */}
      <div className="space-y-2 max-h-72 overflow-y-auto" data-testid="fs-results">
        {loading && <div className="text-center py-6 text-zinc-400"><Loader2 className="h-5 w-5 animate-spin inline" /></div>}
        {!loading && sortedRows.length === 0 && (
          <div className="text-center py-6 text-sm text-zinc-500" data-testid="fs-empty">
            {networkOnly
              ? "No suppliers in your network match. Add suppliers under Suppliers, or use Find supplier."
              : "No suppliers match. Try widening the filters, or add suppliers in the Marketplace / your Network."}
          </div>
        )}
        {!loading && sortedRows.map((r) => {
          const k = `${r.kind}:${r.id}`;
          const on = !!selMap[k];
          const online = isOnline(r);
          return (
            <button
              key={k}
              type="button"
              onClick={() => toggle(r)}
              className={`w-full text-left rounded-lg border p-3 flex items-center gap-3 transition-colors ${on ? "border-ink ring-1 ring-ink bg-white" : "border-zinc-200 bg-white hover:bg-zinc-50"}`}
              data-testid={`fs-candidate-${r.id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm truncate">
                  {r.name}
                  {online
                    ? <Badge className="ml-2 text-[10px] bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Online</Badge>
                    : <Badge variant="secondary" className="ml-2 text-[10px]">Offline</Badge>}
                  {r._rel ? <Badge className="ml-1 text-[10px] bg-emerald-50 text-emerald-700 hover:bg-emerald-50">Best match</Badge> : null}
                </div>
                <div className="text-xs text-zinc-500 truncate">
                  {[r.city, r.country].filter(Boolean).join(", ") || "—"} · {(r.categories || []).filter(Boolean).join(", ") || "—"}
                </div>
              </div>
              {r.average_rating ? (
                <span className="text-xs text-amber-600 inline-flex items-center gap-0.5 whitespace-nowrap" data-testid={`fs-rating-${r.id}`}>
                  <Star className="h-3 w-3 fill-amber-400 stroke-amber-400" />
                  {Number(r.average_rating).toFixed(1)}
                  {r.review_count ? <span className="text-zinc-400"> ({r.review_count})</span> : null}
                </span>
              ) : null}
              <span
                role="button"
                tabIndex={0}
                title="View supplier profile"
                onClick={(e) => openInfo(e, r)}
                onKeyDown={(e) => { if (e.key === "Enter") openInfo(e, r); }}
                className="shrink-0 rounded-full p-1 text-zinc-400 hover:text-sky-600 hover:bg-sky-50 transition-colors"
                data-testid={`fs-info-${r.id}`}
              >
                {infoBusy === r.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Info className="h-4 w-4" />}
              </span>
              {on && <Check className="h-4 w-4 text-ink shrink-0" />}
            </button>
          );
        })}
      </div>

      {profile && <SupplierDetail supplier={profile} onClose={() => setProfile(null)} />}
      {privProfile && <PrivateSupplierInfo supplier={privProfile} onClose={() => setPrivProfile(null)} />}

      {selCount > 0 && (
        <div className="text-xs font-medium text-ink" data-testid="fs-selected-count">
          {selCount} supplier{selCount > 1 ? "s" : ""} selected for quotation
        </div>
      )}
    </div>
  );
}

// Dialog wrapper used from an EXISTING task (Supplier quotes / detail view) —
// same picker as the create-task flow, but sends the quote requests
// immediately to the selected suppliers on confirm.
export function FindSupplierDialog({ jobId, yachtId, defaultCategory, onClose, onInvited }) {
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);

  const send = async () => {
    if (!selected.length) return;
    setBusy(true);
    try {
      await api.post(`/maintenance/${jobId}/quotes`, {
        invites: selected.map((s) => ({ supplier_kind: s.kind, supplier_id: s.id })),
      });
      toast.success(`Quote request sent to ${selected.length} supplier(s)`);
      onInvited?.();
      onClose();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-[calc(100vw-1.5rem)] max-w-xl max-h-[88vh] overflow-y-auto overflow-x-hidden p-4 sm:p-6" data-testid="find-supplier-dialog" onOpenAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader><DialogTitle>Find supplier</DialogTitle></DialogHeader>
        <DialogDescription className="sr-only">Search and select suppliers to request quotes for this task.</DialogDescription>
        <FindSupplierPicker yachtId={yachtId} defaultCategory={defaultCategory} selected={selected} onChange={setSelected} />
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="fs-dialog-cancel">Cancel</Button>
          <Button onClick={send} disabled={!selected.length || busy} className="bg-ink hover:bg-black" data-testid="fs-send-quotes">
            {busy ? "Sending…" : `Request quotes${selected.length ? ` (${selected.length})` : ""}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
